import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PublicLayout } from '../components/PublicLayout';
import { PublicPageSeo } from '../components/PublicPageSeo';
import { RelatedLinks } from '../components/RelatedLinks';
import { getContent } from '../content/i18n';
import { useLocale, useLocalizedPath } from '../hooks/useLocale';
import {
  ShieldCheckIcon,
  GlobeAltIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  StarIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

export const Landing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const content = getContent(locale);
  const { landing } = content;
  const [openFaq, setOpenFaq] = React.useState<number | null>(0);

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const homePath = locale === 'en' ? '/en' : '/';

  return (
    <>
      <PublicPageSeo
        includeSiteSchemas
        faqs={landing.faqs}
        breadcrumbs={[{ name: content.common.home, path: homePath }]}
      />
      <PublicLayout wide>
        <section className="pt-8 pb-16 sm:pt-12 sm:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-primary-600 dark:text-primary-400 font-medium mb-4">{landing.heroTitle}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                {landing.heroHighlight.split('，')[0] || landing.heroHighlight.split(',')[0]}
                <span className="block text-primary-600 dark:text-primary-400">
                  {landing.heroHighlight.includes('，')
                    ? landing.heroHighlight.split('，').slice(1).join('，')
                    : landing.heroHighlight.split(',').slice(1).join(',') || landing.heroHighlight}
                </span>
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
                {landing.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={localizedPath('/register')}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors shadow-lg hover:shadow-xl"
                >
                  {content.common.getStarted}
                </Link>
                <Link
                  to={localizedPath('/features')}
                  className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-8 py-3 rounded-lg text-lg font-medium transition-colors"
                >
                  {content.common.learnMore}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-lg font-semibold text-gray-900 dark:text-white mb-6">
              {landing.platformTitle}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(['windows', 'macos', 'android', 'ios'] as const).map((platform) => (
                <Link
                  key={platform}
                  to={localizedPath(`/download/${platform}`)}
                  className="flex flex-col items-center p-4 bg-white dark:bg-gray-900 rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <DevicePhoneMobileIcon className="h-8 w-8 text-primary-600 dark:text-primary-400 mb-2" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {content.platforms[platform].name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{landing.featuresTitle}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">{landing.featuresSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[ShieldCheckIcon, GlobeAltIcon, BoltIcon, DevicePhoneMobileIcon].map((Icon, i) => (
                <div key={i} className="text-center">
                  <div className="mx-auto h-12 w-12 text-primary-600 dark:text-primary-400 mb-4">
                    <Icon className="h-12 w-12" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {landing.features[i].name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{landing.features[i].description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{landing.howItWorksTitle}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">{landing.howItWorksSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {landing.steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-6">
                    <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to={localizedPath('/register')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors shadow-lg"
              >
                {content.common.getStarted}
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{landing.testimonialsTitle}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">{landing.testimonialsSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {landing.testimonials.map((t, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">&ldquo;{t.content}&rdquo;</p>
                  <p className="font-medium text-gray-900 dark:text-white">{t.author}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{landing.faqTitle}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">{landing.faqSubtitle}</p>
            </div>
            <div className="space-y-3">
              {landing.faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">{faq.question}</span>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="p-4 pt-0 text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary-600 dark:bg-primary-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">{landing.ctaTitle}</h2>
            <p className="text-xl text-primary-100 mb-8">{landing.ctaSubtitle}</p>
            <Link
              to={localizedPath('/register')}
              className="inline-block bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg text-lg font-medium transition-colors shadow-lg"
            >
              {content.common.register}
            </Link>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RelatedLinks
            links={[
              { label: locale === 'zh' ? '套餐价格' : 'Pricing', href: '/pricing' },
              { label: locale === 'zh' ? '客户端下载' : 'Download', href: '/download' },
              { label: locale === 'zh' ? '使用教程' : 'Help Center', href: '/help' },
            ]}
          />
        </div>
      </PublicLayout>
    </>
  );
};
